/**
 * Right-side Drawer to create a Platform Provider Option
 * (Generic External API Provider Onboarding P0).
 */
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  platformCreateProviderOption,
  platformDisableProviderOption,
  platformListExecutionOptionModels,
  platformListExecutionPlanWorkerRuntimes,
  platformListProviderAdapters,
  platformListProviderCredentials,
  platformListWorkerExecutionCapabilities,
  platformSetDefaultProviderOption,
  type ExecutionPlanWorkerRuntimeRow,
  type ProviderAdapterRow,
  type ProviderOptionMappingInput,
  type WorkerExecutionCapabilityRow,
} from '../api'
import type { BindableModelRow } from '../types'
import {
  EXECUTION_PLAN_CREATE_TARGET_TYPES,
  WORKER_DISPATCH_AUTO,
  WORKER_DISPATCH_PINNED,
  formatWorkerRuntimeOptionLabel,
  isWorkerExecutionTarget,
  workerAdvertisesCapability,
} from './executionPlanMappingUi'
import { platformColors } from './theme'

type TargetWorker = { id: string; name: string; status: string; online: boolean }
type TargetProvider = {
  id: string
  provider_key: string
  display_name: string
  provider_type: string
  credential_required: boolean
  // VISTOERA_FUNCTION_NODE_PROVIDER_OPTION_ADAPTER_AUTO_RESOLUTION_P0_V1
  // — server-resolved execution adapter (spec §7/§9). Optional so this
  // component still typechecks against any older cached prop shape.
  execution_target_type?: string
  auto_resolved?: boolean
  adapter_key?: string | null
  adapter_display_label?: string | null
  selectable_adapters?: Array<{ key: string; display_name: string }>
}
type CredentialOption = {
  id: string
  name: string
  provider_key: string
  status: string
  health_status: string | null
  environment: string
}

type Props = {
  open: boolean
  capability: string
  /** Selected Canvas function node display name (context only). */
  nodeDisplayName?: string | null
  /** Selected Canvas function node type, e.g. generate_tryon. */
  nodeType?: string | null
  targetWorkers: TargetWorker[]
  targetProviders: TargetProvider[]
  onClose: () => void
  onCreated: () => void | Promise<void>
}

function errCode(err: unknown): string | null {
  const e = err as { response?: { data?: { detail?: { code?: string } | string } } }
  const detail = e?.response?.data?.detail
  if (detail && typeof detail === 'object' && detail.code) return String(detail.code)
  return null
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography.Text strong style={{ display: 'block', marginBottom: 8, color: platformColors.primaryText }}>
      {children}
    </Typography.Text>
  )
}

function isBindableCredential(c: CredentialOption): boolean {
  return c.status !== 'DISABLED'
}

export default function AddProviderDrawer({
  open,
  capability,
  nodeDisplayName,
  nodeType,
  targetWorkers: _targetWorkers,
  targetProviders,
  onClose,
  onCreated,
}: Props) {
  const { t } = useTranslation('platformAdmin')
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [adapters, setAdapters] = useState<ProviderAdapterRow[]>([])
  const [credentialCatalog, setCredentialCatalog] = useState<CredentialOption[]>([])
  const [credentialsLoading, setCredentialsLoading] = useState(false)
  const [providerModels, setProviderModels] = useState<BindableModelRow[]>([])
  const [providerModelsLoading, setProviderModelsLoading] = useState(false)
  const [workerRuntimes, setWorkerRuntimes] = useState<ExecutionPlanWorkerRuntimeRow[]>([])
  const [workerRuntimesError, setWorkerRuntimesError] = useState<string | null>(null)
  const [workerRuntimesLoading, setWorkerRuntimesLoading] = useState(false)
  const [capabilityOptions, setCapabilityOptions] = useState<WorkerExecutionCapabilityRow[]>([])
  const targetType = Form.useWatch('execution_target_type', form) as string | undefined
  const workerDispatchMode = Form.useWatch('worker_dispatch_mode', form) as string | undefined
  const selectedWorkerId = Form.useWatch('worker_id', form) as string | undefined
  const selectedExecCapability = Form.useWatch('required_worker_capability', form) as string | undefined
  const endpointMode = Form.useWatch('endpoint_mode', form) as string | undefined
  const adapterKey = Form.useWatch('adapter_key', form) as string | undefined
  const providerDefinitionId = Form.useWatch('provider_definition_id', form) as string | undefined
  const enabledWatch = Form.useWatch('enabled', form) as boolean | undefined

  const selectedProvider = useMemo(
    () => targetProviders.find((p) => p.id === providerDefinitionId) ?? null,
    [targetProviders, providerDefinitionId],
  )

  const selectedAdapter = useMemo(
    () => adapters.find((a) => a.key === adapterKey) ?? null,
    [adapters, adapterKey],
  )

  const parameterEntries = useMemo(() => {
    const schema = selectedAdapter?.parameter_schema
    if (!schema || typeof schema !== 'object') return []
    return Object.entries(schema)
  }, [selectedAdapter])

  const mapCredentialError = useCallback(
    (err: unknown) => {
      const code = errCode(err)
      if (code === 'CREDENTIAL_REQUIRED') return t('providers.drawer.errors.credentialRequired')
      if (code === 'CREDENTIAL_ENVIRONMENT_MISMATCH') return t('providers.drawer.errors.environmentMismatch')
      if (code === 'CREDENTIAL_PROVIDER_MISMATCH') return t('providers.drawer.errors.providerMismatch')
      if (code === 'CREDENTIAL_DISABLED') return t('providers.drawer.errors.disabled')
      if (code === 'CREDENTIAL_NOT_FOUND') return t('providers.drawer.errors.notFound')
      if (code === 'MODEL_REQUIRED_FOR_EXTERNAL_API') return t('providers.drawer.errors.modelRequired')
      if (code === 'PROVIDER_MODEL_MISMATCH') return t('providers.drawer.errors.providerModelMismatch')
      if (code === 'MODEL_NOT_ENABLED') return t('providers.drawer.errors.modelNotEnabled')
      if (code === 'MODEL_NOT_FOUND') return t('providers.drawer.errors.modelNotFound')
      if (code === 'MODEL_NOT_COMPATIBLE') return t('providers.drawer.errors.modelNotCompatible')
      const e = err as { response?: { data?: { detail?: { message?: string } | string } }; message?: string }
      const detail = e?.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.message) {
        // Prefer i18n for known legacy English leakage
        if (/requires a credential_id/i.test(detail.message)) {
          return t('providers.drawer.errors.credentialRequired')
        }
        return detail.message
      }
      if (typeof detail === 'string') return detail
      return e?.message || t('providers.createFailed')
    },
    [t],
  )

  const loadAdapters = useCallback(async () => {
    try {
      const res = await platformListProviderAdapters()
      setAdapters(res.data.adapters ?? [])
    } catch {
      setAdapters([])
    }
  }, [])

  const loadCredentialCatalog = useCallback(async () => {
    setCredentialsLoading(true)
    try {
      // One catalog request for the drawer; filter by vendor client-side.
      const res = await platformListProviderCredentials()
      setCredentialCatalog(
        (res.data.credentials ?? []).map((r) => ({
          id: r.id,
          name: r.name,
          provider_key: r.provider_key || '',
          status: r.status,
          health_status: r.health_status,
          environment: r.environment,
        })),
      )
    } catch {
      setCredentialCatalog([])
    } finally {
      setCredentialsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    form.resetFields()
    form.setFieldsValue({
      execution_target_type: 'EXTERNAL_API',
      enabled: true,
      set_default_after_create: false,
      endpoint_mode: 'adapter_default',
      capability,
      preview_credential_id: undefined,
      production_credential_id: undefined,
    })
    void loadAdapters()
    void loadCredentialCatalog()
    if (!open) return
    setWorkerRuntimesLoading(true)
    setWorkerRuntimesError(null)
    void platformListExecutionPlanWorkerRuntimes()
      .then((res) => setWorkerRuntimes(res.data.workers ?? []))
      .catch(() => {
        setWorkerRuntimes([])
        setWorkerRuntimesError('Worker 列表暂不可用')
      })
      .finally(() => setWorkerRuntimesLoading(false))
    void platformListWorkerExecutionCapabilities(capability, 'LOCAL_WORKER')
      .then((res) => setCapabilityOptions(res.data.items ?? []))
      .catch(() => setCapabilityOptions([]))
    form.setFieldsValue({
      worker_dispatch_mode: WORKER_DISPATCH_PINNED,
      required_worker_capability: capability,
    })
  }, [open, capability, form, loadAdapters, loadCredentialCatalog])

  const selectedWorkerRuntime = useMemo(
    () => workerRuntimes.find((w) => w.worker_id === selectedWorkerId) ?? null,
    [workerRuntimes, selectedWorkerId],
  )
  const capabilityMismatchWarning =
    isWorkerExecutionTarget(targetType) &&
    workerDispatchMode === WORKER_DISPATCH_PINNED &&
    selectedWorkerRuntime &&
    selectedExecCapability &&
    !workerAdvertisesCapability(selectedWorkerRuntime.advertised_capabilities, selectedExecCapability)

  const vendorCredentials = useMemo(() => {
    const key = selectedProvider?.provider_key
    if (!key) return []
    return credentialCatalog.filter((c) => c.provider_key === key && isBindableCredential(c))
  }, [credentialCatalog, selectedProvider])

  const credentialScopeLabel = (environment: string) => {
    if (environment === 'SHARED') return t('providers.credentials.scopeShared')
    if (environment === 'PREVIEW') return t('providers.credentials.scopePreviewOnly')
    if (environment === 'PRODUCTION') return t('providers.credentials.scopeProductionOnly')
    return environment
  }

  const toCredentialOption = (c: CredentialOption) => ({
    value: c.id,
    label: `${c.name} · ${credentialScopeLabel(c.environment)}`,
  })

  const previewCredentialOptions = vendorCredentials
    .filter((c) => c.environment === 'SHARED' || c.environment === 'PREVIEW')
    .map(toCredentialOption)

  const productionCredentialOptions = vendorCredentials
    .filter((c) => c.environment === 'SHARED' || c.environment === 'PRODUCTION')
    .map(toCredentialOption)

  useEffect(() => {
    if (!open || !selectedProvider) return
    const previewEligible = vendorCredentials.filter(
      (c) => c.environment === 'SHARED' || c.environment === 'PREVIEW',
    )
    // Auto-select only when exactly one Preview-compatible account exists.
    if (previewEligible.length === 1) {
      const current = form.getFieldValue('preview_credential_id')
      if (!current) {
        form.setFieldsValue({ preview_credential_id: previewEligible[0].id })
      }
    }
  }, [open, selectedProvider, vendorCredentials, form])

  const applyAdapterDefaults = (adapter: ProviderAdapterRow) => {
    const defaults = adapter.model_defaults || {}
    const paramValues: Record<string, unknown> = {}
    for (const [key, spec] of Object.entries(adapter.parameter_schema || {})) {
      if (spec && typeof spec === 'object' && 'default' in spec && spec.default !== undefined) {
        paramValues[key] = spec.default
      } else if (defaults[key] !== undefined) {
        paramValues[key] = defaults[key]
      }
    }
    form.setFieldsValue({
      adapter_key: adapter.key,
      endpoint_mode: 'adapter_default',
      endpoint_override: undefined,
      parameters: paramValues,
    })
  }

  const loadProviderModels = async (providerDefId: string) => {
    setProviderModelsLoading(true)
    try {
      const res = await platformListExecutionOptionModels(capability, providerDefId)
      const items = res.data.items ?? []
      setProviderModels(items)
      if (items.length === 1) {
        form.setFieldsValue({ provider_model_id: items[0].provider_model_id })
      }
    } catch {
      setProviderModels([])
    } finally {
      setProviderModelsLoading(false)
    }
  }

  const onProviderPicked = async (providerDefinitionId: string) => {
    const provider = targetProviders.find((p) => p.id === providerDefinitionId)
    form.setFieldsValue({
      preview_credential_id: undefined,
      production_credential_id: undefined,
      adapter_key: undefined,
      provider_model_id: undefined,
      parameters: undefined,
    })
    setProviderModels([])
    if (!provider) return

    void loadProviderModels(providerDefinitionId)

    let adapterList = adapters
    if (!adapterList.length) {
      try {
        const res = await platformListProviderAdapters()
        adapterList = res.data.adapters ?? []
        setAdapters(adapterList)
      } catch {
        adapterList = []
      }
    }

    // VISTOERA_FUNCTION_NODE_PROVIDER_OPTION_ADAPTER_AUTO_RESOLUTION_
    // P0_V1 (spec §1/§9) — ProviderDefinition already resolved its ONE
    // correct execution adapter server-side; never re-derive a "best
    // guess" adapter from an unrelated, unfiltered global catalog (the
    // exact bug this task fixes — DeepSeek + creative.ideation must
    // never surface FASHN / Alibaba / Local Worker adapters).
    if (provider.auto_resolved) {
      const resolvedKey = provider.adapter_key || undefined
      const legacyMatch = resolvedKey ? adapterList.find((a) => a.key === resolvedKey) : undefined
      if (legacyMatch) {
        applyAdapterDefaults(legacyMatch)
      } else {
        form.setFieldsValue({ adapter_key: resolvedKey })
      }
      return
    }
    // Legacy multi-variant Provider (FASHN today) — admin still picks,
    // but only among THIS Provider's own declared variants; if there is
    // exactly one, pre-select it (never force a choice when there's
    // really nothing to choose).
    const own = (provider.selectable_adapters || [])
      .map((s) => adapterList.find((a) => a.key === s.key))
      .filter((a): a is ProviderAdapterRow => Boolean(a))
    if (own.length === 1) applyAdapterDefaults(own[0])
  }

  const buildMapping = (values: Record<string, unknown>): ProviderOptionMappingInput => {
    const executionTargetType = String(values.execution_target_type)
    if (executionTargetType === 'LOCAL_WORKER') {
      const dispatch = (values.worker_dispatch_mode as string) || WORKER_DISPATCH_PINNED
      const requiredCap = (values.required_worker_capability as string | undefined)?.trim() || capability
      const config: Record<string, unknown> = {
        required_worker_capability: requiredCap,
        dispatch_policy: dispatch === WORKER_DISPATCH_AUTO ? 'ANY_HEALTHY_COMPATIBLE_RUNTIME' : 'PINNED_RUNTIME',
      }
      const sw = (values.required_worker_software_id as string | undefined)?.trim()
      if (sw) config.required_worker_software_id = sw
      return {
        execution_target_type: 'LOCAL_WORKER',
        worker_id: dispatch === WORKER_DISPATCH_PINNED ? (values.worker_id as string | undefined) || null : null,
        provider_definition_id: null,
        credential_id: null,
        adapter_key: null,
        config,
      }
    }

    const previewId = (values.preview_credential_id as string | undefined) || null
    // Production stays unbound unless explicitly selected — never silent fallback.
    const productionId = (values.production_credential_id as string | undefined) || null

    if (executionTargetType === 'EXTERNAL_API') {
      const parameters =
        values.parameters && typeof values.parameters === 'object'
          ? (values.parameters as Record<string, unknown>)
          : {}
      const endpointModeValue = (values.endpoint_mode as string) || 'adapter_default'
      const config: Record<string, unknown> = {
        region: (values.region as string | undefined) || null,
        endpoint_mode: endpointModeValue,
        endpoint_override:
          endpointModeValue === 'custom' ? (values.endpoint_override as string | undefined) || null : null,
        parameters,
        environment_bindings: {
          PREVIEW: { credential_id: previewId },
          PRODUCTION: { credential_id: productionId },
        },
      }
      return {
        execution_target_type: 'EXTERNAL_API',
        worker_id: null,
        provider_definition_id: (values.provider_definition_id as string | undefined) || null,
        // Legacy readers: top-level mirrors Preview binding only when present.
        credential_id: previewId,
        adapter_key: (values.adapter_key as string | undefined) || null,
        provider_model_id: (values.provider_model_id as string | undefined) || null,
        config,
      }
    }

    return {
      execution_target_type: executionTargetType,
      worker_id: null,
      provider_definition_id: (values.provider_definition_id as string | undefined) || null,
      credential_id: previewId,
      adapter_key: (values.adapter_key as string | undefined) || null,
      config: null,
    }
  }

  const submit = async () => {
    const values = await form.validateFields()
    const displayZh = (values.display_name_zh as string | undefined)?.trim() || ''
    const displayEn = (values.display_name_en as string | undefined)?.trim() || ''
    const frontendDisplayName = displayZh || displayEn
    if (!frontendDisplayName) {
      message.error(t('providers.drawer.required'))
      return
    }

    const provider = targetProviders.find((p) => p.id === values.provider_definition_id)
    const enabled = values.enabled !== false
    if (
      values.execution_target_type === 'EXTERNAL_API' &&
      provider?.credential_required &&
      enabled &&
      !values.preview_credential_id
    ) {
      message.error(t('providers.drawer.errors.previewAccountRequired'))
      return
    }

    const mapping = buildMapping(values)
    setSubmitting(true)
    try {
      const created = await platformCreateProviderOption({
        capability,
        frontend_display_name: frontendDisplayName,
        frontend_description: (values.description_zh as string | undefined) || null,
        display_name_zh: displayZh || null,
        display_name_en: displayEn || null,
        description_zh: (values.description_zh as string | undefined) || null,
        description_en: (values.description_en as string | undefined) || null,
        mapping,
      })
      if (values.enabled === false && created.data?.id) {
        await platformDisableProviderOption(created.data.id)
      }
      if (values.set_default_after_create && created.data?.id) {
        await platformSetDefaultProviderOption(created.data.id)
      }
      message.success(t('providers.createSuccess'))
      onClose()
      await onCreated()
    } catch (err) {
      message.error(mapCredentialError(err))
    } finally {
      setSubmitting(false)
    }
  }

  // VISTOERA_FUNCTION_NODE_PROVIDER_OPTION_ADAPTER_AUTO_RESOLUTION_
  // P0_V1 (spec §3 hard rule) — scoped to ONLY the selected Provider's
  // own declared variants (server-resolved `selectable_adapters`).
  // Deliberately NEVER falls back to the full, unfiltered global
  // adapter catalog — that fallback was the exact bug being fixed
  // (DeepSeek surfacing FASHN / Alibaba / Local Worker).
  const adaptersForSelectedProvider = useMemo(() => {
    if (!selectedProvider || selectedProvider.auto_resolved) return []
    const allowedKeys = new Set((selectedProvider.selectable_adapters || []).map((s) => s.key))
    return adapters.filter((a) => allowedKeys.has(a.key))
  }, [adapters, selectedProvider])

  const previewRequired = Boolean(
    targetType === 'EXTERNAL_API' && selectedProvider?.credential_required && enabledWatch !== false,
  )

  return (
    <Drawer
      open={open}
      title={t('providers.addProviderTitle')}
      onClose={onClose}
      width={520}
      destroyOnClose
      data-testid="add-provider-drawer"
      styles={{ body: { paddingBottom: 24 } }}
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{t('providers.drawer.cancel')}</Button>
          <Button type="primary" loading={submitting} onClick={() => void submit()} data-testid="add-provider-submit">
            {t('providers.drawer.submit')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        {(nodeDisplayName || nodeType) && (
          <div
            data-testid="add-provider-node-context"
            style={{
              marginBottom: 14,
              padding: '10px 12px',
              borderRadius: 8,
              background: platformColors.primarySoft,
              border: `1px solid ${platformColors.border}`,
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {t('providers.currentFunctionNode')}
            </Typography.Text>
            <Typography.Text strong style={{ display: 'block', marginTop: 2 }}>
              {nodeDisplayName || nodeType}
            </Typography.Text>
            {(nodeType || capability) && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {[nodeType, capability].filter(Boolean).join(' · ')}
              </Typography.Text>
            )}
          </div>
        )}
        <SectionTitle>{t('providers.drawer.sectionBasic')}</SectionTitle>
        <Form.Item name="capability" label={t('providers.drawer.capability')}>
          <Input disabled />
        </Form.Item>
        <Form.Item
          name="display_name_zh"
          label={t('providers.drawer.displayNameZh')}
          rules={[{ required: true, message: t('providers.drawer.required') }, { max: 200 }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="display_name_en" label={t('providers.drawer.displayNameEn')} rules={[{ max: 200 }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description_zh" label={t('providers.drawer.descriptionZh')}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="description_en" label={t('providers.drawer.descriptionEn')}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item
          name="execution_target_type"
          label={t('providers.drawer.executionTargetType')}
          rules={[{ required: true, message: t('providers.drawer.required') }]}
          initialValue="LOCAL_WORKER"
        >
          <Select
            options={EXECUTION_PLAN_CREATE_TARGET_TYPES.map(({ value, label }) => ({ value, label }))}
            onChange={() => {
              form.setFieldsValue({
                worker_id: undefined,
                provider_definition_id: undefined,
                preview_credential_id: undefined,
                production_credential_id: undefined,
                adapter_key: undefined,
                worker_dispatch_mode: WORKER_DISPATCH_PINNED,
                required_worker_capability: capability,
              })
            }}
          />
        </Form.Item>

        {isWorkerExecutionTarget(targetType) && (
          <>
            <Form.Item
              name="worker_dispatch_mode"
              label="Worker 调度方式"
              rules={[{ required: true, message: t('providers.drawer.required') }]}
              initialValue={WORKER_DISPATCH_PINNED}
            >
              <Select
                options={[
                  { value: WORKER_DISPATCH_PINNED, label: '固定 Worker' },
                  { value: WORKER_DISPATCH_AUTO, label: '自动调度' },
                ]}
                onChange={() => form.setFieldsValue({ worker_id: undefined })}
              />
            </Form.Item>
            {workerDispatchMode === WORKER_DISPATCH_PINNED && (
              <Form.Item
                name="worker_id"
                label="Worker Runtime"
                rules={[{ required: true, message: t('providers.drawer.required') }]}
                extra={workerRuntimesError || undefined}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={workerRuntimesLoading}
                  placeholder={workerRuntimesError || '选择已注册的 Worker Runtime'}
                  options={workerRuntimes.map((w) => ({
                    value: w.worker_id,
                    label: formatWorkerRuntimeOptionLabel(w),
                  }))}
                />
              </Form.Item>
            )}
            <Form.Item
              name="required_worker_capability"
              label="执行能力"
              rules={[{ required: true, message: t('providers.drawer.required') }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={capabilityOptions.map((c) => ({
                  value: c.id,
                  label: `${c.label} (${c.id})${c.currently_advertised ? '' : ' · 暂无 Runtime 自报'}`,
                }))}
              />
            </Form.Item>
            {capabilityMismatchWarning && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="该 Worker 当前未声明此能力。管理员仍可保存此映射，请确认 Worker 实际支持该任务契约。"
              />
            )}
            {workerDispatchMode === WORKER_DISPATCH_AUTO && (
              <Form.Item name="required_worker_software_id" label="Worker Software（可选）">
                <Input placeholder="例如 vistoera-chatgpt-worker" />
              </Form.Item>
            )}
          </>
        )}

        {targetType === 'EXTERNAL_API' && (
          <Form.Item
            name="provider_definition_id"
            label={t('providers.drawer.vendor')}
            rules={[{ required: true, message: t('providers.drawer.required') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={targetProviders.map((p) => ({
                value: p.id,
                label: `${p.display_name} (${p.provider_key})`,
              }))}
              onChange={(id) => void onProviderPicked(id)}
              data-testid="add-provider-vendor-select"
            />
          </Form.Item>
        )}

        {targetType === 'EXTERNAL_API' && (
          <>
            <Divider style={{ margin: '8px 0 16px' }} />
            <SectionTitle>{t('providers.drawer.sectionApi')}</SectionTitle>
            {selectedProvider?.auto_resolved ? (
              <div
                data-testid="add-provider-auto-resolved-adapter"
                style={{
                  marginBottom: 16,
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: platformColors.primarySoft,
                  border: `1px solid ${platformColors.border}`,
                }}
              >
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {t('providers.drawer.executionMethod')}
                </Typography.Text>
                <Typography.Text strong>
                  {selectedProvider.adapter_display_label || selectedProvider.display_name}
                </Typography.Text>
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {t('providers.drawer.autoResolved')}
                </Tag>
              </div>
            ) : selectedProvider ? (
              <Form.Item
                name="adapter_key"
                label={t('providers.drawer.adapterKey')}
                rules={[{ required: true, message: t('providers.drawer.required') }]}
                extra={t('providers.drawer.adapterKeyScopedHint')}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={adaptersForSelectedProvider.map((a) => ({
                    value: a.key,
                    label: `${a.display_name} (${a.key})`,
                  }))}
                  onChange={(key) => {
                    const adapter = adapters.find((a) => a.key === key)
                    if (adapter) {
                      applyAdapterDefaults(adapter)
                    }
                    // VistoeraTASK-00295 — switching Adapter must not
                    // keep a ProviderModel id chosen under another variant.
                    form.setFieldsValue({ provider_model_id: undefined })
                  }}
                />
              </Form.Item>
            ) : null}
            <Form.Item
              name="provider_model_id"
              label={t('providers.drawer.model')}
              rules={[{ required: true, message: t('providers.drawer.required') }]}
              extra={t('providers.drawer.modelSelectHint')}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={providerModelsLoading}
                placeholder={t('providers.drawer.modelPlaceholder')}
                data-testid="add-provider-model-select"
                options={providerModels.map((m) => ({
                  value: m.provider_model_id,
                  label: `${m.display_name}${m.model_external_id ? ` · ${m.model_external_id}` : ''}`,
                }))}
                notFoundContent={
                  providerModelsLoading
                    ? undefined
                    : t('providers.drawer.modelEmpty')
                }
              />
            </Form.Item>
            <Form.Item name="region" label={t('providers.drawer.region')}>
              <Input placeholder="cn-beijing / us-east-1" />
            </Form.Item>
            <Form.Item name="endpoint_mode" label={t('providers.drawer.endpointMode')} initialValue="adapter_default">
              <Select
                options={[
                  { value: 'adapter_default', label: t('providers.drawer.endpointModeAdapterDefault') },
                  { value: 'custom', label: t('providers.drawer.endpointModeCustom') },
                ]}
              />
            </Form.Item>
            {endpointMode === 'custom' && (
              <Form.Item name="endpoint_override" label={t('providers.drawer.endpointOverride')}>
                <Input />
              </Form.Item>
            )}
            {parameterEntries.length > 0 && (
              <>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('providers.drawer.parameters')}
                </Typography.Text>
                {parameterEntries.map(([key, rawSpec]) => {
                  const spec = (rawSpec || {}) as Record<string, unknown>
                  const label = (spec.label as string) || key
                  const type = String(spec.type || 'string')
                  const enumVals = Array.isArray(spec.enum) ? (spec.enum as Array<string | number>) : null
                  if (enumVals) {
                    return (
                      <Form.Item key={key} name={['parameters', key]} label={String(label)}>
                        <Select
                          allowClear
                          options={enumVals.map((v) => ({ value: v as string | number, label: String(v) }))}
                        />
                      </Form.Item>
                    )
                  }
                  if (type === 'boolean') {
                    return (
                      <Form.Item key={key} name={['parameters', key]} label={String(label)} valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    )
                  }
                  if (type === 'integer' || type === 'number') {
                    return (
                      <Form.Item key={key} name={['parameters', key]} label={String(label)}>
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>
                    )
                  }
                  return (
                    <Form.Item key={key} name={['parameters', key]} label={String(label)}>
                      <Input />
                    </Form.Item>
                  )
                })}
              </>
            )}

            {selectedProvider?.credential_required && (
              <>
                <Divider style={{ margin: '8px 0 16px' }} />
                <SectionTitle>{t('providers.drawer.sectionAccounts')}</SectionTitle>
                <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 0 }}>
                  {t('providers.drawer.sameCredentialHint')}
                </Typography.Paragraph>
                <Form.Item
                  name="preview_credential_id"
                  label={t('providers.drawer.previewCredential')}
                  rules={
                    previewRequired
                      ? [{ required: true, message: t('providers.drawer.errors.previewAccountRequired') }]
                      : undefined
                  }
                  extra={
                    !previewCredentialOptions.length && !credentialsLoading
                      ? t('providers.drawer.createAccountNote')
                      : undefined
                  }
                >
                  <Select
                    allowClear={!previewRequired}
                    showSearch
                    loading={credentialsLoading}
                    optionFilterProp="label"
                    options={previewCredentialOptions}
                    placeholder={t('providers.drawer.selectAccount')}
                    data-testid="add-provider-preview-account"
                  />
                </Form.Item>
                <Form.Item
                  name="production_credential_id"
                  label={t('providers.drawer.productionCredential')}
                  extra={t('providers.drawer.productionOptionalHint')}
                >
                  <Select
                    allowClear
                    showSearch
                    loading={credentialsLoading}
                    optionFilterProp="label"
                    options={productionCredentialOptions}
                    placeholder={t('providers.drawer.notConfigured')}
                    data-testid="add-provider-production-account"
                  />
                </Form.Item>
                <Typography.Paragraph style={{ fontSize: 12, marginBottom: 16 }}>
                  <Link to="/platform/provider-credentials" target="_blank" rel="noreferrer">
                    {t('providers.drawer.createAccountLink')}
                  </Link>
                </Typography.Paragraph>
              </>
            )}
          </>
        )}

        <Divider style={{ margin: '8px 0 16px' }} />
        <SectionTitle>{t('providers.drawer.sectionEnable')}</SectionTitle>
        <Form.Item name="enabled" label={t('providers.drawer.enabled')} valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
        <Form.Item name="set_default_after_create" valuePropName="checked">
          <Checkbox>{t('providers.drawer.setDefaultAfterCreate')}</Checkbox>
        </Form.Item>
      </Form>
    </Drawer>
  )
}
